import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  fromSeoUrl,
  toSeoUrl,
  toSingular,
  toPlural,
} from 'helpers/HelperFunctions';
import toSentenceCase from 'helpers/sentenceCase';
import {
  MAX_URL_PAGE,
  buildJobsBatchUrl,
  clampJobsPage,
} from '../jobsPagination';
import getJobsCanonical, {
  jobsListingHasFilterQuery,
} from '../getJobsCanonical';
import buildJobsListingCopy from '../buildJobsListingCopy';

import styles from './ProjectsHeader.module.scss';

const ProjectsHeader = ({
  filter = null,
  getSelectedRoleName,
  config,
  loggedIn,
  liveJobCount = 0,
  batchPage = 1,
  moreToLoad = false,
  locationPathname = '',
  locationSearch = '',
  onJobsBreadcrumbClick = null,
  onRoleBreadcrumbClick = null,
  children = null,
}) => {
  const selectedRoleName = getSelectedRoleName() || '';
  const roleSlug =
    (filter && filter.name) ||
    (selectedRoleName ? toSeoUrl(toPlural(selectedRoleName)) : null);
  const country = (filter && filter.country) || null;
  const city = (filter && filter.city) || null;

  const copy = buildJobsListingCopy({
    role: roleSlug,
    roleDisplayName: selectedRoleName || null,
    country,
    city,
    liveJobCount,
    isLoggedIn: Boolean(loggedIn),
  });

  const baseUrl = (config.baseUrl || '').replace(/\/$/, '');
  const { canonical } = getJobsCanonical({
    role: (filter && filter.name) || null,
    country,
    city,
    query: locationSearch,
    baseUrl,
  });

  const pathname =
    locationPathname || canonical.replace(baseUrl, '') || '/jobs';
  const absoluteBatchUrl = (page) =>
    `${baseUrl}${buildJobsBatchUrl(pathname, locationSearch, page)}`;

  // SEO prev/next use the crawlable URL page (capped at MAX_URL_PAGE)
  const urlBatchPage = clampJobsPage(batchPage);

  const helmetLinks = [
    {
      rel: 'canonical',
      href: canonical,
    },
  ];

  if (urlBatchPage > 1) {
    helmetLinks.push({
      rel: 'prev',
      href: absoluteBatchUrl(urlBatchPage - 1),
    });
  }

  if (moreToLoad && urlBatchPage < MAX_URL_PAGE) {
    helmetLinks.push({
      rel: 'next',
      href: absoluteBatchUrl(urlBatchPage + 1),
    });
  }

  const helmetMeta = [
    { name: 'description', content: copy.metaDescription },
    { property: 'og:title', content: copy.title },
    { property: 'og:description', content: copy.metaDescription },
    {
      property: 'og:url',
      content: canonical,
    },
    { name: 'twitter:title', content: copy.title },
    { name: 'twitter:description', content: copy.metaDescription },
  ];

  // Query filter variants (remote, experience, multi-role, etc.) — not clean
  // role / country / city / role+location path pages.
  if (jobsListingHasFilterQuery(locationSearch)) {
    helmetMeta.push({ name: 'robots', content: 'noindex' });
  }

  const roleDisplayRaw = selectedRoleName
    ? selectedRoleName
    : filter && filter.name
    ? toSentenceCase(fromSeoUrl(filter.name))
    : '';
  // Singular Title Case to match job details ("Animator Jobs", not "Animators Jobs").
  const roleDisplayName = roleDisplayRaw ? toSingular(roleDisplayRaw) : '';
  const roleJobsLabel = roleDisplayName ? `${roleDisplayName} Jobs` : '';
  const locationShort = city
    ? fromSeoUrl(city)
    : country
    ? fromSeoUrl(country)
    : '';

  // Role / location listing pages — same for guests and logged-in users.
  const showBreadcrumb = Boolean(roleJobsLabel || locationShort);

  const titleClassName = [
    styles.title,
    loggedIn ? styles.titleLoggedIn : '',
    showBreadcrumb ? styles.titleWithBreadcrumb : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isModifiedClick = (event) =>
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey ||
    (typeof event.button === 'number' && event.button !== 0);

  const handleCrumbClick = (handler) => (event) => {
    if (!handler) {
      return;
    }
    // Let modified / non-primary clicks follow the link (new tab, etc.).
    if (isModifiedClick(event)) {
      return;
    }
    event.preventDefault();
    handler();
  };

  // Match BriefDetailsFreelancer: underlined links + ">" with ml/mr throughout
  // (avoid Shared Breadcrumbs auto-separator, which mixed spacing).
  const currentCrumbLabel = roleJobsLabel
    ? locationShort
      ? `${roleJobsLabel} in ${locationShort}`
      : roleJobsLabel
    : `Jobs in ${locationShort}`;

  const separator = (
    <span className="ml mr" aria-hidden="true">
      &gt;
    </span>
  );

  return (
    <div>
      <Helmet title={copy.title} link={helmetLinks} meta={helmetMeta} />

      {showBreadcrumb && (
        <div
          className={`mb++ ${styles.jobBreadcrumb}`}
          data-testid="jobs-breadcrumb"
        >
          <Link
            to="/jobs"
            className={styles.jobBreadcrumb}
            onClick={handleCrumbClick(onJobsBreadcrumbClick)}
          >
            Find Freelance Jobs
          </Link>
          {separator}
          {roleJobsLabel && locationShort ? (
            <>
              <Link
                to={`/jobs/${roleSlug}`}
                className={styles.jobBreadcrumb}
                onClick={handleCrumbClick(onRoleBreadcrumbClick)}
              >
                {roleJobsLabel}
              </Link>
              {separator}
              <span>{currentCrumbLabel}</span>
            </>
          ) : (
            <span>{currentCrumbLabel}</span>
          )}
        </div>
      )}

      <h1 className={titleClassName} data-testid="jobs-listing-h1">
        {copy.h1}
      </h1>
      {!loggedIn && copy.intro ? (
        <p className={styles.description}>{copy.intro}</p>
      ) : null}
      {children}
    </div>
  );
};

ProjectsHeader.propTypes = {
  filter: PropTypes.object,
  getSelectedRoleName: PropTypes.func,
  config: PropTypes.object,
  loggedIn: PropTypes.bool,
  liveJobCount: PropTypes.number,
  batchPage: PropTypes.number,
  moreToLoad: PropTypes.bool,
  locationPathname: PropTypes.string,
  locationSearch: PropTypes.string,
  onJobsBreadcrumbClick: PropTypes.func,
  onRoleBreadcrumbClick: PropTypes.func,
  children: PropTypes.node,
};

ProjectsHeader.defaultProps = {
  filter: null,
  liveJobCount: 0,
  batchPage: 1,
  moreToLoad: false,
  locationPathname: '',
  locationSearch: '',
  onJobsBreadcrumbClick: null,
  onRoleBreadcrumbClick: null,
  children: null,
};

export default ProjectsHeader;
